import { api, apiFetch, ApiError } from './client';
import { getSession } from './session-store';
import type {
  ContactDetailDto,
  ContactDto,
  ContactListFilters,
  ContactNoteDto,
  ContactSourceDto,
  ImportResult,
  Page,
} from './types';

export function listContacts(filters: ContactListFilters): Promise<Page<ContactDto>> {
  return api.get<Page<ContactDto>>('/api/v1/contacts', filters as Record<string, string | number | boolean | undefined>);
}

export function getContact(id: string): Promise<ContactDetailDto> {
  return api.get<ContactDetailDto>(`/api/v1/contacts/${id}`);
}

export interface PatchContactBody {
  funnel_stage?: string;
  lost_reason?: string | null;
  tags?: string[];
  owner_user_id?: string | null;
  segment?: string | null;
  kotbot_badge?: string | null;
  source_id?: string | null;
}

export function patchContact(id: string, body: PatchContactBody): Promise<{ contact: ContactDto }> {
  return api.patch<{ contact: ContactDto }>(`/api/v1/contacts/${id}`, body);
}

export function addNote(id: string, text: string): Promise<{ note: ContactNoteDto }> {
  return api.post<{ note: ContactNoteDto }>(`/api/v1/contacts/${id}/notes`, { text });
}

export function importContactsJson(channelId: string, rows: unknown[]): Promise<ImportResult> {
  return apiFetch<ImportResult>('/api/v1/contacts/import', {
    method: 'POST',
    body: rows,
    query: { channel_id: channelId },
  });
}

export const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Raw (non-JSON) POST — shared by the CSV and xlsx import/preview calls, which send the file as the body verbatim. */
async function rawPost<T>(url: string, contentType: string, body: BodyInit): Promise<T> {
  const token = getSession().csrfToken;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': contentType, ...(token ? { 'x-csrf-token': token } : {}) },
    credentials: 'include',
    body,
  });
  if (!res.ok) {
    let message = res.statusText;
    let code = 'unknown_error';
    try {
      const errBody = (await res.json()) as { error?: { code: string; message: string } };
      if (errBody.error) {
        message = errBody.error.message;
        code = errBody.error.code;
      }
    } catch {
      // ignore non-JSON error body
    }
    throw new ApiError(res.status, code, message);
  }
  return (await res.json()) as T;
}

export function importContactsCsv(channelId: string, csv: string): Promise<ImportResult> {
  return rawPost<ImportResult>(`/api/v1/contacts/import?channel_id=${encodeURIComponent(channelId)}`, 'text/csv', csv);
}

export interface ColumnMapping {
  vk_user_id: string;
  first_name?: string;
  last_name?: string;
  tags?: string;
  source?: string;
  segment?: string;
}

export function importContactsXlsx(channelId: string, file: File | Blob, mapping: ColumnMapping): Promise<ImportResult> {
  const url = `/api/v1/contacts/import?channel_id=${encodeURIComponent(channelId)}&mapping=${encodeURIComponent(JSON.stringify(mapping))}`;
  return rawPost<ImportResult>(url, XLSX_CONTENT_TYPE, file);
}

export interface ImportPreview {
  columns: { letter: string; header: string }[];
  rows: string[][];
  rowCount: number;
}

export function previewContactsCsv(channelId: string, csv: string): Promise<ImportPreview> {
  return rawPost<ImportPreview>(`/api/v1/contacts/import/preview?channel_id=${encodeURIComponent(channelId)}`, 'text/csv', csv);
}

export function previewContactsXlsx(channelId: string, file: File | Blob): Promise<ImportPreview> {
  return rawPost<ImportPreview>(`/api/v1/contacts/import/preview?channel_id=${encodeURIComponent(channelId)}`, XLSX_CONTENT_TYPE, file);
}

/** Builds the GET /contacts/export URL (same filters as the list) for a plain navigation download — the session cookie rides along automatically. */
export function contactsExportUrl(format: 'csv' | 'xlsx', filters: ContactListFilters, ids?: string[]): string {
  const url = new URL('/api/v1/contacts/export', window.location.origin);
  url.searchParams.set('format', format);
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && k !== 'cursor' && k !== 'limit') url.searchParams.set(k, String(v));
  }
  if (ids && ids.length > 0) url.searchParams.set('ids', ids.join(','));
  return url.pathname + url.search;
}

export function listContactSources(): Promise<{ sources: ContactSourceDto[] }> {
  return api.get<{ sources: ContactSourceDto[] }>('/api/v1/contact-sources');
}

export function createContactSource(title: string): Promise<{ source: ContactSourceDto }> {
  return api.post<{ source: ContactSourceDto }>('/api/v1/contact-sources', { title });
}

export function renameContactSource(id: string, title: string): Promise<{ source: ContactSourceDto }> {
  return api.patch<{ source: ContactSourceDto }>(`/api/v1/contact-sources/${id}`, { title });
}

export function deleteContactSource(id: string): Promise<void> {
  return api.del<void>(`/api/v1/contact-sources/${id}`);
}
